import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ApiService } from '../service/api.service';
import { RequestService } from '../service/request.service';
import { GetterService } from '../service/getter-service.service';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
} from '@angular/router';
import { UserService } from '../service/user.service';
import { Campaigns, HeaderBanners } from '../../Model/WebCustomer';
import { PosthogService } from '../analytics/services/posthog.service';
import { SearchComponent } from '../search/search.component';
import { loadNewSideCart } from '../lazy-components';
import { headerHeld, headerScrollHold } from '../shared/page-scroll';
import { DESIGN_TWINS } from '../design/design-twins';
// type-only: the cart is loaded lazily (lazy-components.ts)
import type { NewSideCartComponent } from '../new-side-cart/new-side-cart.component';
import { brandFlavor, pageSegment, pageOf, pagePath, brandUrl, movedPagePath } from 'src/app/brand/brand-context';
declare let fbq: any;

@Component({
  selector: 'app-new-header',
  templateUrl: './new-header.component.html',
  styleUrls: ['./new-header.component.scss'],
})
export class NewHeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('sentinel') sentinel: ElementRef<HTMLElement>;
  @ViewChild('bar') bar: ElementRef<HTMLElement>;
  /** the desktop search in the bar (its results panel + scrim render inside
   *  the sticky header, so the bar must not slide away while they are open) */
  @ViewChild(SearchComponent) deskSearch: SearchComponent;

  brandName: string;
  brandFlavor: string;
  tiles: Campaigns[] = [];
  logo: string;
  CurrencySymbol: string;

  MenuWebBanner: string;
  MobileMenuWebBanner: string;
  /** the banner URL failed to load: hide the box instead of a broken-image
      icon in an empty 4:1 slot (reset by the next successful load) */
  bannerBroken = false;
  HeaderBanners: HeaderBanners;
  defaultBanner: string;
  defaultMobileBanner: string;

  /*
   * Parity with the legacy app-header (header.component.ts): the same
   * TemplateData switches decide what the bar offers.
   *  - showSearch   TemplateData.SearchEnabled (search shown unless false)
   *  - showCart     CheckoutDetail.ShowHeaderCartIcon — as legacy: the cart
   *                 shows only when this is true (absent = hidden)
   *  - showAvatar   Header.Login.Image — as legacy: the account avatar shows
   *                 unless this is set to false
   *  - showLogin    legacy: "Log In" unless DistributorLogin; with
   *                 DistributorLogin it shows only when AllowLogin is set, and
   *                 then reads "Distributor Login"
   *  - showRewards  BusinessInfo.HeaderRewardIcon (default true): rewards
   *                 entry for a signed-in customer → join-reward
   *
   * TemplateType plays no part here: the phone ☰ always opens the site side
   * nav. Product categories belong to the shop component (its own Browse
   * drawer on phones, sidebar on desktop), not to the header.
   */
  showSearch = true;
  showCart = false;
  showAvatar = true;
  showLogin = true;
  loginLabel = 'Login';
  showRewards = true;

  isLoggedIn: boolean;
  firstName: string;
  lastName: string;
  webLink: string;

  sideMenu = false;
  isClosing = false;
  showMobileSearch = false;
  accountOpen = false;
  isStuck = false;
  private stuckObserver: IntersectionObserver;

  /**
   * Hide-on-scroll: the bar slides away while the customer scrolls down the
   * page and comes back the moment they scroll up, so it doesn't sit on top
   * of a long menu. Never hides near the top of the page, while a drawer /
   * search / account menu is open, or for a customer who prefers reduced
   * motion (they keep the always-visible bar).
   */
  isHidden = false;
  private lastScrollY = 0;
  private scrollRaf: number;
  private readonly hideAfter = 120; // px scrolled before hiding is allowed
  private readonly scrollSlack = 6; // px of movement before a direction counts
  private readonly onPageScroll = () => {
    cancelAnimationFrame(this.scrollRaf);
    this.scrollRaf = requestAnimationFrame(() => this.trackScroll());
  };
  ShowReferSection: boolean;
  cartCounter$: BehaviorSubject<number>;
  cartTotal$: BehaviorSubject<number>;

  HeaderCartColor: string;
  HeaderTextColor: string;
  HeaderTextHoverColor: string;
  HeaderBackgroundColor: string;
  HeaderSelectedTextColor: string;
  HeaderCartBackgroundColor: string;
  HeaderHoverBackgroundColor: string;
  MobileHeaderSelectedTextColor: string;

  currentComponent: string;
  training = this.route.snapshot.queryParams['training'] == 'true';

  subs: Subscription[] = [];
  constructor(
    private apiService: ApiService,
    private _requestService: RequestService,
    private _local: GetterService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router,
    private posthog: PosthogService,
    private zone: NgZone
  ) {}

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
    this.stuckObserver?.disconnect();
    document.removeEventListener('scroll', this.onPageScroll, true);
    cancelAnimationFrame(this.scrollRaf);
    this.barObserver?.disconnect();
    document.documentElement.style.removeProperty('--ui-hdr-offset');
  }

  /**
   * `html, body { height: 100% }` + `body { overflow: auto }` make <body> the
   * scroll container, so window scroll never fires; a capturing listener on
   * document sees every scroller. Reads whichever of body / html moved.
   */
  private get pageScrollY(): number {
    return Math.max(
      document.body.scrollTop,
      document.documentElement.scrollTop,
      window.scrollY || 0
    );
  }

  private trackScroll() {
    const y = this.pageScrollY;

    // a page is jumping to a section (chip tap): keep the state it asked for
    // instead of following the scroll direction, so the bar doesn't bounce
    if (headerHeld()) {
      this.lastScrollY = y;
      const hide = headerScrollHold.hidden && this.isStuck && y > this.hideAfter;
      if (hide !== this.isHidden) {
        this.setHidden(hide);
        this.publishOffset();
      }
      return;
    }

    const delta = y - this.lastScrollY;
    if (Math.abs(delta) < this.scrollSlack) return;
    this.lastScrollY = y;

    const surfaceOpen =
      this.sideMenu ||
      this.showMobileSearch ||
      this.accountOpen ||
      !!this.deskSearch?.panelOpen;

    // Only hide once the bar is actually stuck to the top of the viewport.
    // With a MenuWebBanner above it the bar is still in normal flow well past
    // `hideAfter`; translating it away then leaves its empty (white) box
    // between the banner and the page. `isStuck` comes from the sentinel
    // observer, so this also covers banners of any height.
    this.setHidden(
      !(y <= this.hideAfter || !this.isStuck || surfaceOpen || delta < 0)
    );
    this.publishOffset();
  }

  /** The scroll listener runs outside Angular (ngAfterViewInit); come back in
      only when the bar actually slides, so its class updates. */
  private setHidden(hide: boolean) {
    if (hide === this.isHidden) return;
    this.zone.run(() => (this.isHidden = hide));
  }

  /**
   * `--ui-hdr-offset` on <html>: how much of the top of the viewport the bar
   * covers right now — its height while showing, 0 while slid away. Page
   * elements that stick under the header (the shop's Browse bar) use
   * `top: var(--ui-hdr-offset, 0px)` with the same 0.28s ease, so they move
   * in step with the bar instead of being covered when it slides back in.
   */
  private barHeight = 0;
  private barObserver: ResizeObserver;

  private publishOffset() {
    const px = this.isHidden ? 0 : this.barHeight;
    document.documentElement.style.setProperty('--ui-hdr-offset', px + 'px');
  }

  ngOnInit(): void {
    this.cartCounter$ = this._requestService.cartCounter$;
    this.cartTotal$ = this._requestService.cartTotal$;

    // the router may already have navigated before this component exists
    this.currentComponent = (pageSegment() || '').split(
      '?'
    )[0];

    this.setupRouterEventListener();
    this.getWebCustomer();
    this.afterLogin();
  }

  setupRouterEventListener() {
    const sub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.currentComponent = pageOf(event.url);

        this.currentComponent = this.currentComponent.split('?')[0];

        const brandName = brandFlavor();
        this._local.set('BrandFlavor', brandName);
        if (
          this.currentComponent != 'login' &&
          this.currentComponent != 'forgot-password' &&
          this.currentComponent != 'registeruser' &&
          this.currentComponent != 'login?guest=false' &&
          this.currentComponent != 'login?training=true' &&
          this.currentComponent != 'guest?training=true' &&
          this.currentComponent != 'guest' &&
          this.currentComponent != 'new-guest-checkout'
        ) {
          this._requestService.redirectToComponent = event.url;
        }
      }

      if (event instanceof NavigationEnd) {
        this.updateBannerImage(this.currentComponent);
        this.closeCart(); // e.g. Checkout went straight through
      }
    });

    this.subs.push(sub);
  }

  getWebCustomer() {
    const sub = this.apiService.getWebcustomer$.subscribe((res) => {
      if (!res) {
        this.userDetailsCheck();
        return;
      }
      this.CurrencySymbol = res.TemplateData?.CurrencySymbol || 'GBP';

      this.webLink = res.TemplateData?.BusinessInfo?.WebLink;

      this.logo = res.BrandData?.BrandBranch?.BrandBranchLogo;

      this.brandName = res.BrandData?.BrandName;

      this.brandFlavor = res.BrandData?.BrandFlavor;

      this.defaultBanner = res.TemplateData?.DynamicStyle?.MenuWebBanner;

      this.defaultMobileBanner =
        res.TemplateData?.DynamicStyle?.MobileMenuWebBanner;

      // still written to storage for other components that read it (as the
      // legacy header does); the header itself doesn't use it
      this._local.set('TemplateType', res.TemplateData?.TemplateType);

      if (typeof res.TemplateData?.SearchEnabled != 'undefined')
        this.showSearch = res.TemplateData.SearchEnabled;

      this.showCart = !!res.TemplateData?.CheckoutDetail?.ShowHeaderCartIcon;

      this.showAvatar = res.TemplateData?.Header?.Login?.Image !== false;

      const distributorLogin = !!res.TemplateData?.CheckoutDetail?.DistributorLogin;
      const allowLogin = !!res.TemplateData?.CheckoutDetail?.AllowLogin;
      this.showLogin = !distributorLogin || allowLogin;
      this.loginLabel = distributorLogin ? 'Distributor login' : 'Login';

      if (typeof res.TemplateData?.BusinessInfo?.HeaderRewardIcon != 'undefined')
        this.showRewards = res.TemplateData.BusinessInfo.HeaderRewardIcon;

      this.ShowReferSection = res.TemplateData?.CheckoutDetail?.ShowReferSection;

      this.HeaderBackgroundColor =
        res.TemplateData?.DynamicStyle?.PrimaryColors?.HeaderBackgroundColor;

      this.HeaderTextColor =
        res.TemplateData?.DynamicStyle?.PrimaryColors?.HeaderTextColor;

      this.HeaderSelectedTextColor =
        res.TemplateData?.DynamicStyle?.PrimaryColors?.HeaderSelectedTextColor;

      this.MobileHeaderSelectedTextColor =
        res.TemplateData?.DynamicStyle?.PrimaryColors?.MobileHeaderSelectedTextColor;

      this.HeaderHoverBackgroundColor =
        res.TemplateData?.DynamicStyle?.PrimaryColors?.HeaderHoverBackgroundColor;

      this.HeaderTextHoverColor =
        res.TemplateData?.DynamicStyle?.PrimaryColors?.HeaderTextHoverColor;

      this.HeaderCartColor =
        res.TemplateData?.DynamicStyle?.TertiaryColors?.HeaderCartColor;

      this.HeaderCartBackgroundColor =
        res.TemplateData?.DynamicStyle?.TertiaryColors?.HeaderCartBackgroundColor;

      this.tiles = res.TemplateData?.HomeTemplate?.Campaigns;

      this.HeaderBanners = res.TemplateData?.DynamicStyle?.HeaderBanners;

      this.updateBannerImage(this.currentComponent);

      if (
        (!this._requestService.redirectToComponent &&
          this.currentComponent == 'login') ||
        this.currentComponent == 'forgot-password' ||
        this.currentComponent == 'registeruser' ||
        this.currentComponent == 'login?guest=false' ||
        this.currentComponent == 'login?training=true'
      ) {
        this._requestService.redirectToComponent = brandUrl(res.TemplateData?.RedirectionConfig?.RedirectionFromCheckout);
      }
    });

    this.subs.push(sub);
  }

  userDetailsCheck() {
    this.isLoggedIn = this._requestService.isLoggedIn();
    this.firstName = this._local.get('CustomerFirstName');
    this.lastName = this._local.get('CustomerLastName');
  }

  updateBannerImage(componentName: string) {
    if (this.defaultBanner) {
      this.MenuWebBanner = this.defaultBanner;
      this.MobileMenuWebBanner = this.defaultMobileBanner;
      return;
    }

    // keyed on the URL as requested (NavigationStart), so the legacy
    // menu / dine-in links that now redirect still pick their banner; the
    // redesigned pages share the banners configured for their legacy twins
    const key = {
      menu: 'Menu',
      'new-menu': 'Menu',
      'dine-in': 'DineIn',
      'new-dine-in': 'DineIn',
      events: 'Event',
      reservation: 'Reservations',
      'new-reservation': 'Reservations',
      bookings: 'Reservations',
      'join-reward': 'Rewards',
      'my-rewards': 'Rewards',
      giftcard: 'GiftCard',
      'gift-cards': 'GiftCard',
      'previous-order': 'MyOrders',
      pay: 'PayOnline',
      login: 'Login',
      shop: 'Shop',
      favourites: 'Favourites',
      online: 'Online',
      products: 'Products',
    }[componentName];

    // pages without a banner of their own keep whatever was showing, as before
    if (!key) return;

    this.MenuWebBanner = this.HeaderBanners?.[key]?.Desktop;
    this.MobileMenuWebBanner = this.HeaderBanners?.[key]?.Mobile;
  }

  ngAfterViewInit(): void {
    // bar height changes with breakpoint / nav strip / wrapping — keep it live
    const bar = this.bar?.nativeElement;
    if (bar) {
      this.barHeight = bar.offsetHeight;
      this.publishOffset();
      if ('ResizeObserver' in window) {
        this.barObserver = new ResizeObserver(() => {
          this.barHeight = bar.offsetHeight;
          this.publishOffset();
        });
        this.barObserver.observe(bar);
      }
    }

    if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.lastScrollY = this.pageScrollY;
      // outside Angular: inside, every scroll event and the frame it
      // schedules ran change detection over the whole page; setHidden()
      // re-enters only when the bar slides in or out
      this.zone.runOutsideAngular(() =>
        document.addEventListener('scroll', this.onPageScroll, {
          capture: true,
          passive: true,
        })
      );
    }

    // shadow under the bar once the banner has scrolled away
    if (!('IntersectionObserver' in window) || !this.sentinel) return;
    this.stuckObserver = new IntersectionObserver(
      ([entry]) => (this.isStuck = !entry.isIntersecting),
      { threshold: 0 }
    );
    this.stuckObserver.observe(this.sentinel.nativeElement);
  }

  /** "AN" for Azam Nawaz; falls back to the first letter of the first name */
  /** the redesigned menu (new-menu / app-new-menu): the banner is capped
      short there so the dishes start near the top of the page */
  get isMenuPage(): boolean {
    return ['new-menu', 'app-new-menu'].includes(this.currentComponent);
  }

  get initials(): string {
    const f = (this.firstName || '').trim();
    const l = (this.lastName || '').trim();
    return ((f[0] || '') + (l[0] || '')).toUpperCase() || '?';
  }

  // Is this nav tab the page we're on? Compares the whole path after the
  // brand, so a tab pointing at "reservation/service-booking" matches it, and
  // treats "new-x" and "x" as the same page, so a "new-reservation" tab stays
  // highlighted on /reservation/service-booking. The old match is kept.
  isActive(tile: Campaigns): boolean {
    const target = (tile?.Redirection || '')
      .split(/[?#]/)[0]
      .replace(/^\/+|\/+$/g, '');
    if (!target) return false;

    if (this.currentComponent && this.currentComponent.includes(target)) {
      return true;
    }

    const page = pagePath();
    // A tile may point at a legacy page that now redirects to its redesigned
    // twin (join-reward / my-rewards → rewards, giftcard → gift-cards,
    // menu → new-menu …). Put both sides through the same map the router
    // uses, then drop the new- prefix, so either spelling lights the tab.
    const canon = (path: string) => {
      const [head, ...rest] = movedPagePath(path).split('/');
      const twin = DESIGN_TWINS[head]?.twin || head;
      return [twin.replace(/^new-/, ''), ...rest].join('/');
    };
    const tab = canon(target);
    const here = canon(page);
    return !!here && (here === tab || here.startsWith(tab + '/'));
  }

  /** navigate and close whatever surface the link was pressed in */
  go(url: string) {
    this.accountOpen = false;
    this.showMobileSearch = false;
    if (this.sideMenu) this.closeSideMenu();
    this.openUrl(url);
  }

  @HostListener('document:click')
  onDocumentClick() {
    this.accountOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.accountOpen = false;
    this.showMobileSearch = false;
    if (this.sideMenu) this.closeSideMenu();
    this.closeCart();
  }

  openSideMenu() {
    this.isClosing = false;
    this.sideMenu = true;
  }

  afterLogin() {
    const sub = this.userService.isLoggedIn$.subscribe((status) => {
      this.isLoggedIn = status;
      if (status) this.userDetailsCheck();
    });
    this.subs.push(sub);
  }

  closeSideMenu() {
    if (this.isClosing) return;
    this.isClosing = true;
    setTimeout(() => {
      this.sideMenu = false;
      this.isClosing = false;
    }, 300); // match the animation duration
  }

  signOut() {
    this.accountOpen = false;
    if (this.sideMenu) this.closeSideMenu();
    this.userService.logout();
  }

  redirect(link) {
    if (link.Redirection) {
      this.openUrl(link.Redirection);
      return;
    }
    location.href = link.WebLink;
  }

  openUrl(url: string) {
    this.router.navigateByUrl(this._requestService.setUrl(url));
  }

  /* ---- cart drawer ------------------------------------------------------
     The header cart shows the cart: app-new-side-cart in a drawer from the
     right. Its own Checkout closes the drawer (checkout output) and opens the
     recommendations once per visit, then checkout — the Checkout analytics
     (fbq 'Checkout') fire there, not on opening the cart. Training mode keeps
     its direct route to checkout. */
  cartOpen = false;
  cartClosing = false;
  readonly loadNewSideCart = loadNewSideCart;

  onCartCreated(ref: ComponentRef<NewSideCartComponent>) {
    ref.instance.checkout.subscribe(() => this.closeCart());
  }

  openCart() {
    const cart = this._requestService.getCartRecipeItems();
    this.posthog.captureEvent('cart_opened_from_header', {
      CartTotal: this.getCartTotal(),
      CartRecipeItems: cart?.length,
    });

    if (this.training) {
      this.router.navigate([this._requestService.setUrl('checkout')], {
        queryParams: { training: true },
      });
      return;
    }

    this.accountOpen = false;
    this.showMobileSearch = false;
    if (this.sideMenu) this.closeSideMenu();
    this.cartClosing = false;
    this.cartOpen = true;
  }

  closeCart() {
    if (!this.cartOpen || this.cartClosing) return;
    this.cartClosing = true;
    setTimeout(() => {
      this.cartOpen = false;
      this.cartClosing = false;
    }, 300); // match the drawer animation
  }

  getCartTotal() {
    let cart = this._requestService.getCartRecipeItems();

    return cart.reduce((total, item) => {
      return total + item.recipeTotal;
    }, 0);
  }
}
